import { Input } from "@/components/ui/input";
import { cn } from "@/libs/utils";
import { CombinedRow } from "@/types/itemsheet";
import { useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";

// 규격서 테이블에 존재하는 편집 가능한 필드들
const editableFields: (keyof CombinedRow)[] = [
  "product_name", // 제품명
  "product_id", // 제품번호
  "specification", // 규격
  "manufacturer", // 제조사
  "vendor", // 벤더
  "buyer", // 구매업체
  "quantity", // 수량
  "unit", // 단위
  "list_price", // 판매가
  "unit_cost", // 매입가
];

const numberFields: (keyof CombinedRow)[] = [
  "quantity",
  "unit_cost",
  "list_price",
  "total_cost",
  "total_list_price",
];

const priceFields: (keyof CombinedRow)[] = [
  "unit_cost",
  "list_price",
  "total_cost",
  "total_list_price",
];

type Props = {
  value: string | number | null;
  isEditing: boolean;
  rowIndex: number;
  accessorKey: keyof CombinedRow;
  originalValue?: string | number | null;
};

export default function EditableCell({
  value,
  isEditing,
  rowIndex,
  accessorKey,
  originalValue,
}: Props) {
  const { control, setValue, getValues } = useFormContext<{
    rows: CombinedRow[];
  }>();

  const fieldPath = `rows.${rowIndex}.${accessorKey}` as const;

  const isNumberField = numberFields.includes(accessorKey);
  const isPriceField = priceFields.includes(accessorKey);
  const isEditableField = editableFields.includes(accessorKey);

  const setValueAs = useMemo(() => {
    return (v: any) => {
      if (v === "") return null;
      if (isNumberField) {
        const num = Number(v);
        return isNaN(num) ? null : num;
      }
      return v;
    };
  }, [isNumberField]);

  if (!isEditing || !isEditableField) {
    // 가격 표시 처리
    const displayValue = (() => {
      if (isPriceField && value != null && value !== "") {
        const numValue = Number(value);
        return !isNaN(numValue) ? numValue.toLocaleString() : value;
      }
      return value;
    })();

    return <>{displayValue}</>;
  }

  return (
    <Controller
      key={fieldPath}
      control={control}
      name={fieldPath}
      defaultValue={value ?? ""}
      render={({ field }) => {
        const currentValue =
          typeof field.value === "string" || typeof field.value === "number"
            ? field.value
            : field.value == null
              ? ""
              : String(field.value);

        // 원본 값과 현재 값 비교
        const compareValue =
          originalValue !== undefined ? originalValue : value;
        const isChanged =
          String(currentValue ?? "") !== String(compareValue ?? "");

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value;
          const parsed = setValueAs(raw);
          field.onChange(parsed);
        };

        return (
          <Input
            {...field}
            value={currentValue}
            type={isNumberField ? "number" : "text"}
            onChange={handleChange}
            className={cn(
              "h-8 w-full border border-gray-300 rounded px-2 py-1",
              "bg-transparent border-none"
            )}
          />
        );
      }}
    />
  );
}
